import React, { useState, useEffect } from 'react'
import { Form, Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import FormContainer from '../components/FormContainer'
import Loader from '../components/Loader'
import Message from '../components/Message'
import { createPaymentType, updatePaymentType, getPaymentTypeDetails } from '../actions/paymentActions'
import { PAYMENT_TYPE_CREATE_RESET, PAYMENT_TYPE_UPDATE_RESET } from '../constants/paymentConstants'

const PAYMENT_NAME_OPTIONS = ['Venmo', 'Zelle', 'PayPal', 'Cash', 'Credit Card']

function PaymentTypeEditScreen() {
    const { id } = useParams()
    const isEditing = Boolean(id)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [name, setName] = useState('')
    const [instructions, setInstructions] = useState('')

    const paymentTypeDetails = useSelector(state => state.paymentTypeDetails)
    const { loading: loadingDetails, error: errorDetails, paymentType } = paymentTypeDetails

    const paymentTypeCreate = useSelector(state => state.paymentTypeCreate)
    const { loading: loadingCreate, error: errorCreate, success: successCreate } = paymentTypeCreate

    const paymentTypeUpdate = useSelector(state => state.paymentTypeUpdate)
    const { loading: loadingUpdate, error: errorUpdate, success: successUpdate } = paymentTypeUpdate

    // Fetch payment type when component mounts or ID changes
    useEffect(() => {
        if (successCreate || successUpdate) {
            dispatch({ type: PAYMENT_TYPE_CREATE_RESET })
            dispatch({ type: PAYMENT_TYPE_UPDATE_RESET })
            navigate('/admin/payment-types')
        }
    }, [dispatch, navigate, successCreate, successUpdate])

    // Fetch details when editing
    useEffect(() => {
        if (isEditing && (!paymentType || paymentType.id !== Number(id))) {
            dispatch(getPaymentTypeDetails(id))
        }
    }, [dispatch, id, isEditing])

    // Populate form when payment type data arrives
    useEffect(() => {
        if (isEditing && paymentType && paymentType.id === Number(id)) {
            setName(paymentType.name)
            setInstructions(paymentType.instructions || '')
        }
    }, [paymentType, id, isEditing])

    const submitHandler = (e) => {
        e.preventDefault()
        const payload = { name, instructions }
        if (isEditing) {
            dispatch(updatePaymentType(id, payload))
        } else {
            dispatch(createPaymentType(payload))
        }
    }

    return (
        <FormContainer>
            <h2>{isEditing ? 'Edit Payment Type' : 'Add Payment Type'}</h2>

            {(loadingDetails || loadingCreate || loadingUpdate) && <Loader />}
            {(errorDetails || errorCreate || errorUpdate) && (
                <Message variant='danger'>{errorDetails || errorCreate || errorUpdate}</Message>
            )}

            <Form onSubmit={submitHandler}>
                <Form.Group className='mb-3' controlId='name'>
                    <Form.Label>Name</Form.Label>
                    <Form.Select value={name} onChange={e => setName(e.target.value)} required>
                        <option value=''>-- Select --</option>
                        {PAYMENT_NAME_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group className='mb-4' controlId='instructions'>
                    <Form.Label>Instructions <span className='text-muted'>(optional)</span></Form.Label>
                    <Form.Control
                        as='textarea'
                        rows={3}
                        placeholder='e.g. Include tournament name in memo'
                        value={instructions}
                        onChange={e => setInstructions(e.target.value)}
                    />
                </Form.Group>

                <Button type='submit' variant='dark'>
                    {isEditing ? 'Save Changes' : 'Create'}
                </Button>
                <Button variant='outline-secondary' className='ms-2' onClick={() => navigate('/admin/payment-types')}>
                    Cancel
                </Button>
            </Form>
        </FormContainer>
    )
}

export default PaymentTypeEditScreen
